import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import { Formik } from 'formik'
import React from 'react'
import { states } from 'us'
import { object, string } from 'yup'
const STATES = Object.keys(states).map(state => ({
  label: state,
  value: state,
}))
const PROPERTY_TYPE = process.env.REACT_APP_SALESFORCE_PROPERTY_TYPE_ID

const schema = object().shape({
  [PROPERTY_TYPE]: string().required(),
  city: string().required(),
  country_code: string().required(),
  debug: string().required(),
  debugEmail: string()
    .email('Enter a valid email address.')
    .required('Email is required.'),
  description: string().notRequired(),
  email: string()
    .email('Enter a valid email address.')
    .required('Email is required.'),
  first_name: string().required(),
  last_name: string().required(),
  lead_source: string().required(),
  oid: string().required(),
  phone: string().required(),
  recordType: string().required(),
  state_code: string().required(),
  street: string().required(),
  zip: string().required(),
})

const RequestFoodsbyForm = ({
  city,
  fieldProps = {},
  handleSubmit,
  state,
  street,
  zip,
  textProps,
}) => {
  return (
    <Formik
      gtm-data-elm-id="LoginForm"
      initialValues={{
        [PROPERTY_TYPE]: '',
        city: city || '',
        country_code: 'US',
        debug: '1',
        debugEmail: 'buildings@foodsby.com',
        description: '',
        email: '',
        first_name: '',
        last_name: '',
        lead_source: 'Foodsby Website',
        oid: process.env.REACT_APP_SALESFORCE_OID,
        phone: '',
        recordType: '012f4000000YP7O',
        state_code: state || '',
        street: street || '',
        zip: zip || '',
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        touched,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" {...textProps}>
            Request Foodsby
          </Typography>
          <TextField
            autoComplete="address-line1"
            error={touched.street && errors.street}
            fullWidth
            id="street"
            label="Street"
            margin="dense"
            name="street"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.street}
            variant="outlined"
            color="secondary"
            {...fieldProps}
          />
          <TextField
            autoComplete="address-level2"
            error={touched.city && errors.city}
            fullWidth
            id="city"
            label="City"
            margin="dense"
            name="city"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.city}
            variant="outlined"
            color="secondary"
            {...fieldProps}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                autoComplete="address-level1"
                error={touched.state_code && errors.state_code}
                fullWidth
                id="state_code"
                label="State"
                margin="dense"
                name="state_code"
                onBlur={handleBlur}
                onChange={handleChange}
                select
                value={values.state_code}
                variant="outlined"
                color="secondary"
                {...fieldProps}
              >
                {STATES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                autoComplete="postal-code"
                error={touched.zip && errors.zip}
                fullWidth
                id="zip"
                label="Zip"
                margin="dense"
                name="zip"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.zip}
                variant="outlined"
                color="secondary"
                {...fieldProps}
              />
            </Grid>
          </Grid>
          <Box py="12px">
            <Typography variant="subtitle2" {...textProps}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  autoComplete="given-name"
                  error={touched.first_name && errors.first_name}
                  fullWidth
                  id="first_name"
                  label="First Name"
                  margin="dense"
                  name="first_name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.first_name}
                  variant="outlined"
                  color="secondary"
                  {...fieldProps}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  autoComplete="family-name"
                  error={touched.last_name && errors.last_name}
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  margin="dense"
                  name="last_name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.last_name}
                  variant="outlined"
                  color="secondary"
                  {...fieldProps}
                />
              </Grid>
            </Grid>
            <TextField
              autoComplete="email"
              error={touched.email && errors.email}
              fullWidth
              id="email"
              label="Email"
              margin="dense"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="email"
              value={values.email}
              variant="outlined"
              color="secondary"
              {...fieldProps}
            />
            <TextField
              autoComplete="tel"
              error={touched.phone && errors.phone}
              fullWidth
              id="phone"
              label="Phone"
              margin="dense"
              name="phone"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.phone}
              variant="outlined"
              color="secondary"
              {...fieldProps}
            />
          </Box>
          <Box py="8px">
            <Typography variant="subtitle2" {...textProps}>
              Type of location
            </Typography>
            {touched[PROPERTY_TYPE] && errors[PROPERTY_TYPE] && (
              <FormHelperText error>Please make a selection</FormHelperText>
            )}
            <RadioGroup
              aria-label="type of location"
              id={PROPERTY_TYPE}
              name={PROPERTY_TYPE}
              onBlur={handleBlur}
              onChange={evt => {
                setFieldValue(PROPERTY_TYPE, evt.target.value)
              }}
              value={values[PROPERTY_TYPE]}
              {...fieldProps}
            >
              <FormControlLabel control={<Radio size="small" />} label="Home" value="Home" />
              <Divider variant="middle" />
              <FormControlLabel
                control={<Radio size="small" />}
                label="Apartment"
                value="Apartment"
              />
              <Divider variant="middle" />
              <FormControlLabel
                control={<Radio size="small" />}
                label="Office Building"
                value="Office Building"
              />
              <Divider variant="middle" />
              <FormControlLabel control={<Radio size="small" />} label="Other" value="Other" />
            </RadioGroup>
            <TextField
              error={touched.description && errors.description}
              fullWidth
              id="description"
              label="Comments"
              margin="dense"
              multiline
              name="description"
              onBlur={handleBlur}
              onChange={handleChange}
              rows="3"
              type="text"
              value={values.description}
              variant="outlined"
              color="secondary"
              {...fieldProps}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" py="2px">
            <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
              SUBMIT REQUEST
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  )
}

export default RequestFoodsbyForm
